import { getStudioSessionSyncUrl } from '@atlassian/studio-entry-link';

export type ScheduledChatFrequency = 'minutely' | 'hourly' | 'daily' | 'weekdays' | 'custom';

export const SCHEDULED_CHAT_MIN_MINUTES = 5;
export const SCHEDULED_CHAT_MAX_MINUTES = 99_999;
export const SCHEDULED_CHAT_DEFAULT_MINUTES: number = SCHEDULED_CHAT_MIN_MINUTES;

export function isValidScheduledChatMinuteInterval(interval: number): boolean {
	return (
		Number.isInteger(interval) &&
		interval >= SCHEDULED_CHAT_MIN_MINUTES &&
		interval <= SCHEDULED_CHAT_MAX_MINUTES
	);
}

export const SCHEDULED_CHAT_DAYS = [
	'monday',
	'tuesday',
	'wednesday',
	'thursday',
	'friday',
	'saturday',
	'sunday',
] as const;

export type ScheduledChatDayOfWeek = (typeof SCHEDULED_CHAT_DAYS)[number];

export type ScheduledChatPayload = {
	timezone?: string;
	name: string;
	prompt: string;
	frequency: ScheduledChatFrequency;
	daysOfWeek: ScheduledChatDayOfWeek[];
	time: string;
	/** Exact recurrence interval when frequency is minutely; omitted values default to 5. */
	intervalMinutes?: number;
	/** Present after successful automation rule creation (Studio SC3 rule UUID). */
	ruleIdUuid?: string;
};

export type ScheduledChatBackendPayload = {
	timezone?: string;
	prompt: string;
	scheduleExpression: string;
	title?: string;
	/** Populated after successful create via assist response {@link Message.message_metadata}. */
	ruleIdUuid?: string;
	rRule?: string;
};

const DAY_MAP: Record<ScheduledChatDayOfWeek, string> = {
	sunday: 'SUN',
	monday: 'MON',
	tuesday: 'TUE',
	wednesday: 'WED',
	thursday: 'THU',
	friday: 'FRI',
	saturday: 'SAT',
};

const REVERSE_DAY_MAP: Record<string, ScheduledChatDayOfWeek> = {
	SUN: 'sunday',
	MON: 'monday',
	TUE: 'tuesday',
	WED: 'wednesday',
	THU: 'thursday',
	FRI: 'friday',
	SAT: 'saturday',
};

// Cron orders named weekdays from Sunday through Saturday.
const CRON_DAYS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

function expandCronDays(dayOfWeek: string): string[] {
	const expandedDays = dayOfWeek.split(',').flatMap((part) => {
		const token = part.trim();
		const endpoints = token.split('-');
		const start = CRON_DAYS.indexOf(endpoints[0]);
		const end = CRON_DAYS.indexOf(endpoints[1] ?? endpoints[0]);

		if (endpoints.length > 2 || start === -1 || end === -1 || start > end) {
			throw new Error(`Unsupported cron day expression: ${token}`);
		}

		return CRON_DAYS.slice(start, end + 1);
	});

	return Array.from(new Set(expandedDays));
}

export function convertToBackendPayload(
	frontendPayload: ScheduledChatPayload,
): ScheduledChatBackendPayload {
	const { name, prompt, frequency, daysOfWeek, time, timezone } = frontendPayload;
	if (frequency === 'minutely') {
		const interval = frontendPayload.intervalMinutes ?? SCHEDULED_CHAT_DEFAULT_MINUTES;
		if (!isValidScheduledChatMinuteInterval(interval)) {
			throw new Error('Schedule interval must be between 5 and 99999 minutes');
		}
		return {
			prompt,
			scheduleExpression: `*/${interval} * * * *`,
			title: name,
			...(timezone !== undefined ? { timezone } : {}),
		};
	}

	let hours: number;
	let minutes: number;

	if (time.includes('AM') || time.includes('PM')) {
		// Handle AM/PM format
		const [timePart, period] = time.split(' ');
		const [hoursStr, minutesStr] = timePart.split(':');
		hours = parseInt(hoursStr);
		minutes = parseInt(minutesStr);

		// Convert to 24-hour format
		if (period === 'PM' && hours !== 12) {
			hours += 12;
		} else if (period === 'AM' && hours === 12) {
			hours = 0;
		}
	} else {
		// Handle 24-hour format
		const [hoursStr, minutesStr] = time.split(':');
		hours = parseInt(hoursStr);
		minutes = parseInt(minutesStr);
	}

	let cronExpression: string;

	switch (frequency) {
		case 'hourly':
			cronExpression = `${minutes} * * * *`;
			break;

		case 'daily':
			cronExpression = `${minutes} ${hours} * * *`;
			break;

		case 'weekdays':
			cronExpression = `${minutes} ${hours} * * MON-FRI`;
			break;

		case 'custom':
			const daysList = daysOfWeek.map((day) => DAY_MAP[day]).join(',');
			cronExpression = `${minutes} ${hours} * * ${daysList}`;
			break;

		default:
			throw new Error(`Unsupported frequency: ${frequency}`);
	}

	return {
		prompt,
		scheduleExpression: cronExpression,
		title: name,
		...(timezone !== undefined ? { timezone } : {}),
	};
}

export function convertFromBackendPayload(
	backendPayload: ScheduledChatBackendPayload,
): ScheduledChatPayload {
	const { prompt, scheduleExpression, title, timezone } = backendPayload;

	const cronParts = scheduleExpression.trim().split(/\s+/);
	if (cronParts.length !== 5) {
		throw new Error(`Invalid cron expression: ${scheduleExpression}`);
	}

	const [minutes, hours, , , dayOfWeek] = cronParts;
	const intervalMatch = minutes.match(/^\*\/(\d+)$/);
	if (intervalMatch && cronParts.slice(1).every((part) => part === '*')) {
		return {
			name: title || 'Scheduled Chat',
			prompt,
			frequency: 'minutely',
			intervalMinutes: Number(intervalMatch[1]),
			daysOfWeek: [],
			time: '09:00',
			...(timezone !== undefined ? { timezone } : {}),
			...(backendPayload.ruleIdUuid ? { ruleIdUuid: backendPayload.ruleIdUuid } : {}),
		};
	}

	// Format time in AM/PM format
	const hoursNum = parseInt(hours);
	const minutesNum = parseInt(minutes);
	const ampm = hoursNum >= 12 ? 'PM' : 'AM';
	const hours12 = hoursNum % 12 || 12; // Convert 0 to 12 for 12 AM
	const time = `${hours12}:${minutesNum.toString().padStart(2, '0')} ${ampm}`;

	let frequency: ScheduledChatFrequency;
	let daysOfWeek: ScheduledChatDayOfWeek[];

	if (hours === '*') {
		frequency = 'hourly';
		daysOfWeek = [];
	} else if (dayOfWeek === '*') {
		frequency = 'daily';
		daysOfWeek = [];
	} else if (dayOfWeek === 'MON-FRI') {
		frequency = 'weekdays';
		daysOfWeek = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
	} else {
		frequency = 'custom';
		daysOfWeek = expandCronDays(dayOfWeek).map((day) => REVERSE_DAY_MAP[day]);
	}

	return {
		name: title || 'Scheduled Chat',
		...(timezone !== undefined ? { timezone } : {}),
		prompt,
		frequency,
		daysOfWeek,
		time,
		...(backendPayload.ruleIdUuid !== undefined && backendPayload.ruleIdUuid !== ''
			? { ruleIdUuid: backendPayload.ruleIdUuid }
			: {}),
	};
}

// Studio SC3 API Integration
const isStaging = () => false;

export function getStudioNextAutomationUiOrigin(): string {
	// Source the Studio host from the sanctioned entry-link helper (no hardcoded URLs).
	return getStudioSessionSyncUrl(isStaging() ? 'staging' : 'production', undefined, undefined);
}

export function buildStudioAutomationRuleEditUrl(
	workspaceId: string,
	spaceId: string,
	ruleIdUuid: string,
): string {
	const base = `/w/${workspaceId}/spaces/${spaceId}/automation#`;
	return `${base}/rule/${encodeURIComponent(ruleIdUuid)}`;
}

export function buildStudioAutomationRuleEditAbsoluteUrl(
	workspaceId: string,
	spaceId: string,
	ruleIdUuid: string,
): string {
	return `${getStudioNextAutomationUiOrigin()}${buildStudioAutomationRuleEditUrl(
		workspaceId,
		spaceId,
		ruleIdUuid,
	)}`;
}

export const STUDIO_SC3_SCHEDULED_CHATS_MOCK_WORKSPACE_ID = 'b78e328b-be49-4a4e-82b6-5158df2cb77f';
export const STUDIO_SC3_SCHEDULED_CHATS_MOCK_SPACE_ID = '14c7f07b-60c0-4a38-bbaa-510e961f3866';

function buildStudioSc3RestPrefix(workspaceId: string, spaceId: string): string {
	return `/gateway/api/automation/sc3/o/studio/t/workspace/i/${workspaceId}/internal-api/pro/rest/${spaceId}`;
}

export function buildStudioSc3RulesBasePath(workspaceId: string, spaceId: string): string {
	return `${buildStudioSc3RestPrefix(workspaceId, spaceId)}/rule`;
}

export function buildStudioSc3RuleResourcePath(
	workspaceId: string,
	spaceId: string,
	ruleId: string,
): string {
	return `${buildStudioSc3RulesBasePath(workspaceId, spaceId)}/${ruleId}`;
}

export type CreateStudioSc3RuleParams = {
	workspaceId: string;
	spaceId: string;
	ruleName: string;
	prompt: string;
	cronExpression: string;
	authorAccountId: string;
	cloudId: string;
};

export type CreateStudioSc3RuleResponse = {
	id: string;
	name: string;
	state: string;
};

export async function createStudioSc3Rule({
	workspaceId,
	spaceId,
	ruleName,
	prompt,
	cronExpression,
	authorAccountId,
	cloudId,
}: CreateStudioSc3RuleParams): Promise<Response> {
	const url = buildStudioSc3RulesBasePath(workspaceId, spaceId);

	const connectionId = crypto.randomUUID();
	const currentTime = new Date().toISOString();
	const containerId = `__NEW__${Date.now()}`;

	const ruleConfig = {
		ruleConfigBean: {
			name: ruleName,
			isNewRule: true,
			state: 'ENABLED',
			canOtherRuleTrigger: false,
			notifyOnError: 'FIRSTERROR',
			authorAccountId,
			actor: {
				type: 'ACCOUNT_ID',
				value: authorAccountId,
			},
			created: currentTime,
			updated: currentTime,
			components: [
				{
					id: `__NEW__COMPONENT__${crypto.randomUUID()}`,
					type: 'rovo.start-agent-conversation.action',
					component: 'ACTION',
					value: {
						agentInstruction: prompt,
					},
					connectionId, // FIXED: Using the same connection ID
					children: [],
					conditions: [],
				},
			],
			writeAccessType: 'OWNER_ONLY',
			collaborators: [],
			trigger: {
				id: '__NEW__TRIGGER',
				type: 'scheduled',
				component: 'TRIGGER',
				value: {
					executionMode: 'nosearch',
					schedule: {
						rate: 0,
						rateInterval: 86400,
						cronExpression: '',
						method: 'BASIC',
						rRule: buildRRuleFromCron(cronExpression),
					},
				},
				children: [],
				conditions: [],
			},
			labels: [],
			projects: [],
			ruleScope: {
				resources: [`ari:cloud:studio::space/${workspaceId}/${spaceId}`],
			},
			clientKey: '',
			partitionId: null,
			description: '',
			tags: [],
			billingType: 'NORMAL',
		},
		newConnections: [
			{
				id: connectionId,
				connectionTargetKey: 'com.atlassian.rovo',
				accountId: authorAccountId,
				targetConfigJson: JSON.stringify({
					cloudId: cloudId,
					product: 'atlassian-agent-studio',
				}),
				authType: 'ASAP',
				authUserSource: 'CONFIG',
				container: {
					id: containerId,
					type: 'RULE',
				},
				createdAt: currentTime,
				updatedAt: currentTime,
			},
		],
	};

	return fetch(url, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			Accept: '*/*',
		},
		credentials: 'include',
		body: JSON.stringify(ruleConfig),
	});
}

// Helper function to build RRule from cron expression
function buildRRuleFromCron(cronExpression: string): string {
	const cronParts = cronExpression.split(' ');
	if (cronParts.length !== 5) {
		throw new Error(`Invalid cron expression: ${cronExpression}`);
	}

	const [minutes, hours, dayOfMonth, month, dayOfWeek] = cronParts;

	const now = new Date();
	let startTime = new Date(now);

	const tzid = Intl.DateTimeFormat().resolvedOptions().timeZone;

	let rruleFreq = '';
	let rruleParams = '';

	// Check for minutely patterns (every X minutes)
	if (minutes.includes('/')) {
		const interval = parseInt(minutes.split('/')[1]);
		startTime = new Date(now.getTime() + 60000); // Start 1 minute from now
		rruleFreq = 'MINUTELY';
		rruleParams = `INTERVAL=${interval};BYSECOND=0;COUNT=5`;
	}
	// Handle hourly patterns (every hour or every X hours)
	else if (hours === '*' || hours.includes('/')) {
		const interval = hours.includes('/') ? parseInt(hours.split('/')[1]) : 1;
		const targetMinutes = minutes === '*' ? 0 : parseInt(minutes);

		startTime.setMinutes(targetMinutes, 0, 0);
		if (startTime <= now) {
			startTime.setHours(startTime.getHours() + interval);
		}

		rruleFreq = 'HOURLY';
		rruleParams = `INTERVAL=${interval};BYSECOND=0`;
	}
	// Handle daily and specific time patterns
	else if (hours !== '*' && minutes !== '*') {
		const targetHours = parseInt(hours);
		const targetMinutes = parseInt(minutes);

		startTime.setHours(targetHours, targetMinutes, 0, 0);

		// If the time has already passed today, schedule for tomorrow
		if (startTime <= now) {
			startTime.setDate(startTime.getDate() + 1);
		}

		const isDaily = dayOfWeek === '*' && dayOfMonth === '*';
		const isCustomDays = dayOfWeek !== '*';
		const isMonthly = month !== '*' && dayOfMonth !== '*';

		if (isDaily) {
			// Daily
			rruleFreq = 'DAILY';
			rruleParams = `BYHOUR=${targetHours};BYMINUTE=${targetMinutes};BYSECOND=0;INTERVAL=1`;
		}

		if (isCustomDays) {
			// Custom days
			const dayMap: Record<string, string> = {
				SUN: 'SU',
				MON: 'MO',
				TUE: 'TU',
				WED: 'WE',
				THU: 'TH',
				FRI: 'FR',
				SAT: 'SA',
			};
			const byDay = expandCronDays(dayOfWeek)
				.map((day) => dayMap[day])
				.join(',');
			rruleFreq = 'WEEKLY';
			rruleParams = `BYDAY=${byDay};BYHOUR=${targetHours};BYMINUTE=${targetMinutes};BYSECOND=0`;
		}

		if (isMonthly) {
			// Monthly or specific day of month
			rruleFreq = 'MONTHLY';
			rruleParams = `INTERVAL=1;BYHOUR=${targetHours};BYMINUTE=${targetMinutes};BYSECOND=0;BYMONTHDAY=${dayOfMonth}`;
		}
	} else {
		// Fallback for unsupported patterns
		throw new Error(`Unsupported cron expression: ${cronExpression}`);
	}

	const dtstart = formatDTStart(startTime);
	return `BEGIN:VEVENT\nDTSTART;TZID=${tzid}:${dtstart}\nRRULE:FREQ=${rruleFreq};${rruleParams}\nEND:VEVENT\n`;
}

function formatDTStart(date: Date): string {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');
	const hour = String(date.getHours()).padStart(2, '0');
	const minute = String(date.getMinutes()).padStart(2, '0');
	const second = String(date.getSeconds()).padStart(2, '0');

	return `${year}${month}${day}T${hour}${minute}${second}`;
}

// ========================================
// Edit, Delete, and Pause/Stop functionality
// Moved from studio-scheduled-rules-api.ts to avoid cyclical dependencies
// ========================================

/**
 * Build the PUT resource path for full-rule replacement.
 * Full-rule `PUT` only — `internal-api/pro`. Scheduled-chat editor uses JSON `PATCH` ({@link patchStudioSc3RuleJsonPatch}); `PUT` is for other integrations.
 */
export function buildStudioSc3RulePutResourcePath(
	workspaceId: string,
	spaceId: string,
	ruleId: string,
): string {
	return `${buildStudioSc3RestPrefix(workspaceId, spaceId)}/rule/${encodeURIComponent(ruleId)}`;
}

/** RFC 6902 replace op as used by Studio SC3 rule updates. */
export type StudioSc3JsonPatchReplaceOperation = {
	op: 'replace';
	path: string;
	value: unknown;
};

/** Pause: set automation rule state to disabled. */
export const STUDIO_SC3_JSON_PATCH_PAUSE_RULE_STATE: readonly StudioSc3JsonPatchReplaceOperation[] =
	[{ op: 'replace', path: '/state', value: 'DISABLED' }];

/** Resume: re-enable a paused rule (`ENABLED` matches SC3 rule update payloads). */
export const STUDIO_SC3_JSON_PATCH_RESUME_RULE_STATE: readonly StudioSc3JsonPatchReplaceOperation[] =
	[{ op: 'replace', path: '/state', value: 'ENABLED' }];

export type PatchStudioSc3RuleJsonPatchParams = {
	workspaceId: string | undefined;
	spaceId: string | undefined;
	ruleId: string;
	patch: readonly StudioSc3JsonPatchReplaceOperation[];
};

/**
 * Updates a rule via JSON Patch body (`PATCH`, e.g. replace `/state` with `DISABLED` / `ENABLED`).
 */
export async function patchStudioSc3RuleJsonPatch({
	workspaceId,
	spaceId,
	ruleId,
	patch,
}: PatchStudioSc3RuleJsonPatchParams): Promise<Response> {
	if (!workspaceId || !spaceId) {
		throw new Error('workspaceId and spaceId are required for rule operations');
	}
	const url = buildStudioSc3RuleResourcePath(workspaceId, spaceId, ruleId);
	return fetch(url, {
		method: 'PATCH',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify(patch),
	});
}

export type PutStudioSc3RuleParams = {
	workspaceId: string;
	spaceId: string;
	ruleId: string;
	body: Record<string, unknown>;
};

/**
 * Full document replace (`PUT .../rule/{ruleId}`).
 * Scheduled-chat rename/edit uses {@link patchStudioSc3RuleJsonPatch} only; retain this for other callers that need a full rule document.
 */
export async function putStudioSc3Rule({
	workspaceId,
	spaceId,
	ruleId,
	body,
}: PutStudioSc3RuleParams): Promise<Response> {
	const url = buildStudioSc3RulePutResourcePath(workspaceId, spaceId, ruleId);
	return fetch(url, {
		method: 'PUT',
		headers: { 'Content-Type': 'application/json', Accept: '*/*' },
		body: JSON.stringify(body),
	});
}

export type DeleteStudioSc3RuleParams = {
	workspaceId: string;
	spaceId: string;
	ruleId: string;
};

/**
 * Delete a scheduled chat rule (`DELETE .../rule/{ruleId}`).
 */
export async function deleteStudioSc3Rule({
	workspaceId,
	spaceId,
	ruleId,
}: DeleteStudioSc3RuleParams): Promise<Response> {
	const url = buildStudioSc3RuleResourcePath(workspaceId, spaceId, ruleId);
	return fetch(url, {
		method: 'DELETE',
		headers: {
			'Content-Type': 'application/json',
			Accept: '*/*',
		},
	});
}

// ========================================
// Studio Info functionality
// Moved from scheduled-chat-studio-info.ts for shared access
// ========================================

/** Assist gateway path — GET returns JSON with workspace and space ids for Studio automation rules. */
export const SCHEDULED_CHAT_STUDIO_INFO_GATEWAY_PATH =
	'/gateway/api/assist/api/rovo/v1/scheduled-chat/studio-info';

/** Normalized ids for Studio SC3 paths (`workspaceId` / `spaceId` query params). */
export type ScheduledChatStudioInfo = {
	workspaceId: string;
	spaceId: string;
};

/**
 * Maps GET `scheduled-chat/studio-info` JSON into {@link ScheduledChatStudioInfo}.
 *
 * Backend shape (among supported variants):
 * - `studioWorkspaceId` + `defaultSpaceId` (canonical Rovo response)
 * - `workspaceId` / `spaceId`, snake_case, or `studioSpaceId` fallbacks for tests / older payloads.
 */
export function parseScheduledChatStudioInfo(data: unknown): ScheduledChatStudioInfo | null {
	if (!data || typeof data !== 'object') {
		return null;
	}
	const r = data as Record<string, unknown>;
	const workspaceRaw =
		(typeof r.studioWorkspaceId === 'string' && r.studioWorkspaceId) ||
		(typeof r.workspaceId === 'string' && r.workspaceId) ||
		(typeof r.workspace_id === 'string' && r.workspace_id);
	const spaceRaw =
		(typeof r.defaultSpaceId === 'string' && r.defaultSpaceId) ||
		(typeof r.spaceId === 'string' && r.spaceId) ||
		(typeof r.space_id === 'string' && r.space_id) ||
		(typeof r.studioSpaceId === 'string' && r.studioSpaceId);
	const workspaceId = typeof workspaceRaw === 'string' ? workspaceRaw.trim() : '';
	const spaceId = typeof spaceRaw === 'string' ? spaceRaw.trim() : '';
	if (!workspaceId || !spaceId) {
		return null;
	}
	return { workspaceId, spaceId };
}

/** Required by the assist gateway (same family as Convo feedback). */
export type FetchScheduledChatStudioInfoParams = {
	/** `X-Product` — from `useGlobalConfig().productHeader` (host `product` in config). */
	productHeader: string;
	/** `X-Experience-Id` */
	experienceId: string;
	/** `X-Cloudid` — site / cloud id from `useGlobalConfig().siteId`. */
	cloudId: string;
};

function buildScheduledChatStudioInfoRequestHeaders(
	params: FetchScheduledChatStudioInfoParams,
): Record<string, string> {
	const headers: Record<string, string> = {
		'X-Product': params.productHeader.trim(),
	};
	if (params.experienceId.trim()) {
		headers['X-Experience-Id'] = params.experienceId.trim();
	}
	if (params.cloudId.trim()) {
		headers['X-Cloudid'] = params.cloudId.trim();
	}
	return headers;
}

// ─── Edit / patch helpers ────────────────────────────────────────────────────

export const ROVO_START_AGENT_CONVERSATION_ACTION_TYPE = 'rovo.start-agent-conversation.action';

/** VEVENT block for `trigger.value.schedule.rRule` (Studio SC3 scheduled trigger). */
export function buildVeventScheduleBlockFromScheduledChatPayload(
	payload: ScheduledChatPayload,
	existingRRule?: string,
): string {
	if (payload.frequency === 'minutely') {
		const interval = payload.intervalMinutes ?? SCHEDULED_CHAT_DEFAULT_MINUTES;
		if (!isValidScheduledChatMinuteInterval(interval)) {
			throw new Error('Schedule interval must be between 5 and 99999 minutes');
		}
		const existingStart = existingRRule?.split(/\r?\n/).find((line) => line.startsWith('DTSTART'));
		const dtStart =
			existingStart ?? `DTSTART:${new Date().toISOString().replace(/[-:]/g, '').split('.')[0]}Z`;
		return `BEGIN:VEVENT\n${dtStart}\nRRULE:FREQ=MINUTELY;INTERVAL=${interval};BYSECOND=0\nEND:VEVENT\n`;
	}
	const FRONTEND_DAY_TO_RRULE: Record<string, string> = {
		sunday: 'SU',
		monday: 'MO',
		tuesday: 'TU',
		wednesday: 'WE',
		thursday: 'TH',
		friday: 'FR',
		saturday: 'SA',
	};

	const [hs, ms] = (payload.time ?? '09:00').split(':');
	const h = Math.min(23, Math.max(0, Number.parseInt(hs ?? '9', 10) || 0));
	const mi = Math.min(59, Math.max(0, Number.parseInt(ms ?? '0', 10) || 0));
	const pad = (n: number) => String(n).padStart(2, '0');
	const dtStart = `DTSTART:20000101T${pad(h)}${pad(mi)}00Z`;

	let rrule: string;
	switch (payload.frequency) {
		case 'hourly':
			rrule = 'RRULE:FREQ=HOURLY;INTERVAL=1';
			break;
		case 'daily':
			rrule = 'RRULE:FREQ=DAILY;INTERVAL=1';
			break;
		case 'weekdays':
			rrule = 'RRULE:FREQ=WEEKLY;INTERVAL=1;BYDAY=MO,TU,WE,TH,FR';
			break;
		case 'custom': {
			const days = [...new Set(payload.daysOfWeek)].sort(
				(a, b) => SCHEDULED_CHAT_DAYS.indexOf(a) - SCHEDULED_CHAT_DAYS.indexOf(b),
			);
			if (days.length === 0) {
				rrule = 'RRULE:FREQ=DAILY;INTERVAL=1';
			} else {
				const byday = days.map((d) => FRONTEND_DAY_TO_RRULE[d]).join(',');
				rrule = `RRULE:FREQ=WEEKLY;INTERVAL=1;BYDAY=${byday}`;
			}
			break;
		}
		default:
			rrule = 'RRULE:FREQ=DAILY;INTERVAL=1';
	}

	return `BEGIN:VEVENT\n${dtStart}\n${rrule}\nEND:VEVENT`;
}

function applyScheduleRRuleToTrigger(trigger: unknown, rRule: string): void {
	if (!trigger || typeof trigger !== 'object') {
		return;
	}
	const value = (trigger as { value?: Record<string, unknown> }).value;
	if (!value || typeof value !== 'object') {
		return;
	}
	const schedule = { ...((value.schedule as Record<string, unknown> | undefined) ?? {}) };
	schedule.method = schedule.method ?? 'BASIC';
	schedule.rRule = rRule;
	if ('rrule' in schedule) {
		schedule.rrule = rRule;
	}
	(value as { schedule: unknown }).schedule = schedule;
}

function findScheduledChatActionComponentIndex(components: unknown): number {
	if (!Array.isArray(components)) {
		return -1;
	}
	for (let i = 0; i < components.length; i++) {
		const c = components[i] as { type?: string } | null | undefined;
		if (c && typeof c === 'object' && c.type === ROVO_START_AGENT_CONVERSATION_ACTION_TYPE) {
			return i;
		}
	}
	return -1;
}

/**
 * JSON Patch (RFC 6902) ops for the **single-rule** `PATCH` resource.
 * Returns `null` when a surgical patch is not safe (e.g. no `rovo.start-agent-conversation.action` component).
 */
export function buildScheduledChatStudioSc3RuleJsonPatch(
	listRow: Record<string, unknown>,
	payload: ScheduledChatPayload,
	automationState: 'ENABLED' | 'DISABLED',
): StudioSc3JsonPatchReplaceOperation[] | null {
	const inner = (listRow.ruleSummary ?? listRow) as Record<string, unknown>;
	const components = inner.components;
	const i = findScheduledChatActionComponentIndex(components);
	if (i < 0) {
		return null;
	}
	if (inner.trigger === undefined || inner.trigger === null) {
		return null;
	}
	const originalTrigger = inner.trigger as {
		value?: { schedule?: { rRule?: string; rrule?: string } };
	};
	const originalSchedule = originalTrigger.value?.schedule;
	const rRule = buildVeventScheduleBlockFromScheduledChatPayload(
		payload,
		originalSchedule?.rRule ?? originalSchedule?.rrule,
	);
	const comp = (components as unknown[])[i] as {
		type?: string;
		value?: Record<string, unknown>;
	};
	if (!comp || typeof comp !== 'object') {
		return null;
	}
	const prevValue = comp.value && typeof comp.value === 'object' ? { ...comp.value } : {};
	const name = String(payload.name ?? inner.name ?? '');
	const promptText = String(payload.prompt ?? '');
	const nextValue =
		comp.type === ROVO_START_AGENT_CONVERSATION_ACTION_TYPE
			? { ...prevValue, agentInstruction: promptText }
			: { ...prevValue, prompt: promptText };
	const trigger = JSON.parse(JSON.stringify(inner.trigger)) as Record<string, unknown>;
	applyScheduleRRuleToTrigger(trigger, rRule);

	return [
		{ op: 'replace', path: '/name', value: name },
		{ op: 'replace', path: '/state', value: automationState },
		{ op: 'replace', path: `/components/${i}/value`, value: nextValue },
		{ op: 'replace', path: '/trigger', value: trigger },
	];
}

export type FetchStudioSc3RuleParams = {
	workspaceId: string;
	spaceId: string;
	ruleId: string;
};

/**
 * Fetches a single Studio SC3 rule document by its ruleId.
 * The returned JSON can be passed directly to {@link buildScheduledChatStudioSc3RuleJsonPatch}.
 */
export async function fetchStudioSc3Rule({
	workspaceId,
	spaceId,
	ruleId,
}: FetchStudioSc3RuleParams): Promise<Record<string, unknown> | null> {
	try {
		const res = await fetchStudioSc3RuleResponse({ workspaceId, spaceId, ruleId });
		return res?.ok ? ((await res.json()) as Record<string, unknown>) : null;
	} catch {
		return null;
	}
}

/** Preserves HTTP status so missing rules can be distinguished from failed requests. */
export async function fetchStudioSc3RuleResponse({
	workspaceId,
	spaceId,
	ruleId,
}: FetchStudioSc3RuleParams): Promise<Response | null> {
	if (!workspaceId || !spaceId || !ruleId) {
		return null;
	}
	const url = buildStudioSc3RuleResourcePath(workspaceId, spaceId, ruleId);
	try {
		return await fetch(url, {
			method: 'GET',
			headers: { 'Content-Type': 'application/json' },
			credentials: 'include',
			cache: 'no-store',
		});
	} catch {
		return null;
	}
}

/**
 * Loads Studio workspace + space ids for scheduled-chat automation API paths.
 * Returns `null` if required headers are missing, the request fails, or the payload is invalid.
 *
 * Note: This function uses fetch directly instead of performHttpFetch to avoid adding
 * a dependency on @atlassian/conversation-assistant-utils in the message-actions-api package.
 */
export async function fetchScheduledChatStudioInfo(
	params: FetchScheduledChatStudioInfoParams,
): Promise<ScheduledChatStudioInfo | null> {
	if (!params.productHeader?.trim()) {
		return null;
	}
	try {
		const response = await fetch(SCHEDULED_CHAT_STUDIO_INFO_GATEWAY_PATH, {
			method: 'GET',
			headers: buildScheduledChatStudioInfoRequestHeaders(params),
			credentials: 'include',
		});

		if (!response.ok) {
			return null;
		}

		const data = await response.json();
		return parseScheduledChatStudioInfo(data);
	} catch {
		return null;
	}
}
